import { create } from 'zustand';

/**
 * User-controlled UI preferences. Loaded eagerly so first paint matches
 * the saved motion settings before React mounts.
 */

const STORAGE_KEY = 'paas_settings';

export interface SettingsState {
  /** Push-notification opt-in. Native only; web shows nothing. */
  pushEnabled: boolean;
  /** Biometric-unlock opt-in. Native only. */
  biometricEnabled: boolean;
  /** Honor `prefers-reduced-motion` regardless of OS setting. */
  reducedMotion: boolean;

  setPushEnabled: (enabled: boolean) => void;
  setBiometricEnabled: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;

  hydrate: () => void;
}

function persist(state: Pick<SettingsState, 'pushEnabled' | 'biometricEnabled' | 'reducedMotion'>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  pushEnabled: false,
  biometricEnabled: false,
  reducedMotion: false,

  setPushEnabled(pushEnabled) {
    set({ pushEnabled });
    persist({ ...get(), pushEnabled });
  },
  setBiometricEnabled(biometricEnabled) {
    set({ biometricEnabled });
    persist({ ...get(), biometricEnabled });
  },
  setReducedMotion(reducedMotion) {
    set({ reducedMotion });
    persist({ ...get(), reducedMotion });
  },

  hydrate() {
    // Node (logic tests) has no storage; the store keeps its defaults there.
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Pick<SettingsState, 'pushEnabled' | 'biometricEnabled' | 'reducedMotion'>;
    set({
      pushEnabled: parsed.pushEnabled,
      biometricEnabled: parsed.biometricEnabled,
      reducedMotion: parsed.reducedMotion,
    });
  },
}));

/** Hydrate eagerly so dark class lands before first paint. */
useSettingsStore.getState().hydrate();
